import React, {useEffect, useMemo, useState} from 'react'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Stack,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Tooltip,
  Avatar,
  Paper,
  Badge,
  Switch,
  Autocomplete,
} from '@mui/material'
import {useGetAllActiveMerchantsIdAndNameCachedQuery} from '../../redux/commonApiCalls'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import AddIcon from '@mui/icons-material/Add'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import PublicIcon from '@mui/icons-material/Public'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'

import {useBulkServiceability, SHIPMENT_TYPE_B2B, pincodeTypesFor} from './useBulkServiceability'

/* ─────────────────────────  STATUS DRAWER  ───────────────────────── */
// shipmentType 'B2B' (PTL): pickup + drop only; no L1 / merchant scope (B2B allocation does not read them).
export function StatusDrawer({open, accountCode, onClose, shipmentType}) {
  const {fetchAccountCodeStatus} = useBulkServiceability()
  const isB2b = shipmentType === SHIPMENT_TYPE_B2B
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !accountCode) return
    setLoading(true)
    fetchAccountCodeStatus(accountCode).then((data) => {
      setStatus(data)
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountCode])

  const isWhitelist = status?.l1MerchantScope === 'WHITELIST'

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      icon={<VisibilityOutlinedIcon />}
      iconColor='primary'
      eyebrow='Account Code'
      title={accountCode || ''}
      subtitle='Live serviceability snapshot'
    >
      {loading && <CenteredLoader />}
      {!loading && status && (
        <Stack spacing={2.5}>
          {!status.isActive && (
            <SectionPaper sx={{borderColor: '#ffcdd2', backgroundColor: '#fff5f5'}}>
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <Avatar sx={{bgcolor: '#ffebee', color: '#c62828', width: 36, height: 36}}>
                  <CancelIcon fontSize='small' />
                </Avatar>
                <Box sx={{flex: 1}}>
                  <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: '#b71c1c'}}>
                    Deactivated
                  </Typography>
                  <Typography sx={{fontSize: '0.72rem', color: '#c62828'}}>
                    {isB2b
                      ? 'Not used by B2B allocation. Upload a pincode file to make it available again.'
                      : 'Hidden from L1 lookups. Reactivate to make available again.'}
                  </Typography>
                </Box>
              </Stack>
            </SectionPaper>
          )}

          {/* L1 status summary */}
          {!isB2b && (
            <SectionPaper>
              <Stack direction='row' spacing={1.5} alignItems='center'>
                <Avatar
                  sx={{
                    bgcolor: status.l1Enabled ? '#e8f5e9' : '#f5f5f5',
                    color: status.l1Enabled ? '#2e7d32' : 'grey.600',
                    width: 40,
                    height: 40,
                  }}
                >
                  <PowerSettingsNewIcon fontSize='small' />
                </Avatar>
                <Box sx={{flex: 1}}>
                  <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: 'grey.800'}}>
                    L1 {status.l1Enabled ? 'Enabled' : 'Disabled'}
                  </Typography>
                  <Typography sx={{fontSize: '0.72rem', color: 'grey.600'}}>
                    {status.l1Enabled
                      ? `Serviceability checks are active for ${status.l1ActiveFor || '—'}`
                      : 'Pincodes loaded but L1 toggle is off'}
                  </Typography>
                </Box>
                <Chip
                  size='small'
                  label={isWhitelist ? `Whitelist (${status.l1EnabledMerchantIds?.length || 0})` : 'All merchants'}
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    backgroundColor: isWhitelist ? '#fff3e0' : '#e3f2fd',
                    color: isWhitelist ? '#e65100' : '#1565c0',
                  }}
                />
              </Stack>
            </SectionPaper>
          )}

          {/* Whitelisted merchants */}
          {!isB2b && isWhitelist && status.l1EnabledMerchantIds?.length > 0 && (
            <SectionPaper>
              <SectionHeader
                icon={<GroupOutlinedIcon fontSize='small' />}
                title='Whitelisted Merchants'
                count={status.l1EnabledMerchantIds.length}
                color='warning'
              />
              <Stack direction='row' spacing={0.75} flexWrap='wrap' useFlexGap sx={{mt: 1.5}}>
                {status.l1EnabledMerchantIds.map((id) => (
                  <Chip
                    key={id}
                    size='small'
                    label={id}
                    sx={{
                      borderRadius: '16px',
                      fontWeight: 600,
                      fontSize: '0.72rem',
                      backgroundColor: '#fff3e0',
                      color: '#e65100',
                    }}
                  />
                ))}
              </Stack>
            </SectionPaper>
          )}

          {/* Pincode counts as KPI tiles */}
          <SectionPaper>
            <SectionHeader
              icon={<FormatListBulletedIcon fontSize='small' />}
              title='Pincode Coverage'
              color='primary'
            />
            <Box
              sx={{
                mt: 1.5,
                display: 'grid',
                gridTemplateColumns: isB2b ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                gap: 1.5,
              }}
            >
              <KpiTile
                label='Pickup'
                value={status.pickupPincodeCount}
                accent='#1565c0'
                bg='#e3f2fd'
              />
              <KpiTile
                label={isB2b ? 'Drop' : 'Drop Prepaid'}
                value={status.dropPrepaidPincodeCount}
                accent='#2e7d32'
                bg='#e8f5e9'
              />
              {!isB2b && (
                <KpiTile
                  label='Drop COD'
                  value={status.dropCodPincodeCount}
                  accent='#e65100'
                  bg='#fff3e0'
                />
              )}
            </Box>
          </SectionPaper>
        </Stack>
      )}
    </DrawerShell>
  )
}

/* ─────────────────────────  HISTORY DRAWER  ───────────────────────── */
export function HistoryDrawer({open, accountCode, onClose, shipmentType}) {
  const {fetchAccountCodeHistory} = useBulkServiceability()
  const {labels: pincodeTypeLabels} = pincodeTypesFor(shipmentType)
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open || !accountCode) return
    setLoading(true)
    fetchAccountCodeHistory(accountCode).then((data) => {
      setHistory(data)
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountCode])

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      icon={<HistoryOutlinedIcon />}
      iconColor='secondary'
      eyebrow='Upload History'
      title={accountCode || ''}
      subtitle={`${history.length} upload${history.length === 1 ? '' : 's'} recorded`}
      width={560}
    >
      {loading && <CenteredLoader />}
      {!loading && history.length === 0 && (
        <EmptyState
          icon={<InboxOutlinedIcon sx={{fontSize: 28}} />}
          title='No upload history yet'
          subtitle='Pincode uploads for this account code will appear here.'
        />
      )}
      {!loading && history.length > 0 && (
        <SectionPaper sx={{p: 0, overflow: 'hidden'}}>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  {['When', 'Type', 'Count', 'By', 'File'].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.68rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: 'grey.700',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #eeeeee',
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((h, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{fontSize: '0.72rem'}}>
                      {h.uploadedAt ? new Date(h.uploadedAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size='small'
                        label={pincodeTypeLabels[h.pincodeType] || h.pincodeType}
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{fontSize: '0.78rem', fontWeight: 700}}>
                      {h.pincodeCount?.toLocaleString()}
                    </TableCell>
                    <TableCell sx={{fontSize: '0.72rem'}}>{h.uploadedBy || '—'}</TableCell>
                    <TableCell>
                      {h.s3Url ? (
                        <Tooltip title={h.fileName || 'Open in new tab'}>
                          <IconButton
                            size='small'
                            component='a'
                            href={h.s3Url}
                            target='_blank'
                            rel='noreferrer'
                            sx={{color: '#1565c0'}}
                          >
                            <OpenInNewIcon sx={{fontSize: 16}} />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography sx={{fontSize: '0.7rem', color: 'grey.400'}}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </SectionPaper>
      )}
    </DrawerShell>
  )
}

/* ─────────────────────────  MERCHANT SCOPE DRAWER  ───────────────────────── */
export function MerchantScopeDrawer({open, accountCode, onClose, onUpdated}) {
  const {fetchAccountCodeStatus, updateMerchantScope} = useBulkServiceability()
  const {data: allMerchants = []} = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [scope, setScope] = useState('ALL')
  const [whitelist, setWhitelist] = useState([])
  // Picked is now a list — the Autocomplete is in multiple-select mode so users
  // can pick many merchants at once, then click Add to commit them all to the
  // whitelist in one go (faster than the old one-at-a-time flow).
  const [picked, setPicked] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !accountCode) return
    // Reset the local edit state every time the drawer opens for a new account
    // code — otherwise picked merchants leak across rows.
    setPicked([])
    setLoading(true)
    fetchAccountCodeStatus(accountCode).then((data) => {
      setStatus(data)
      setScope(data?.l1MerchantScope || 'ALL')
      setWhitelist(data?.l1EnabledMerchantIds || [])
      setLoading(false)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accountCode])

  // id → merchant lookup so chips can show the merchant's name
  const merchantById = useMemo(() => {
    const m = {}
    ;(allMerchants || []).forEach((mer) => {
      if (mer?.merchantId != null) m[mer.merchantId] = mer
    })
    return m
  }, [allMerchants])

  // Active, non-deleted, not-already-picked options.
  // We INCLUDE rows that lack merchantId so the user can at least see the
  // master list — Add is disabled for those records.
  const availableMerchants = useMemo(() => {
    const used = new Set(whitelist)
    const list = Array.isArray(allMerchants) ? allMerchants : []
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[MerchantScopeDrawer] merchants from query:', list.length, list.slice(0, 3))
    }
    return list
      .filter((m) => m && m.is_deleted !== '1') // is_deleted is a "0"/"1" string in this codebase
      .filter((m) => m.merchantId == null || !used.has(m.merchantId))
      .sort((a, b) => (a.name || a.companyName || '').localeCompare(b.name || b.companyName || ''))
  }, [allMerchants, whitelist])

  const addPicked = () => {
    if (!picked || picked.length === 0) return
    const newIds = picked
      .filter((p) => p?.merchantId != null)
      .map((p) => Number(p.merchantId))
      .filter((id) => !whitelist.includes(id))
    if (newIds.length === 0) {
      setPicked([])
      return
    }
    setWhitelist((p) => [...p, ...newIds])
    setPicked([])
  }

  const removeId = (id) => setWhitelist((p) => p.filter((x) => x !== id))

  const onSave = async () => {
    setSaving(true)
    const body = scope === 'ALL' ? {scope: 'ALL'} : {scope: 'WHITELIST', merchantIds: whitelist}
    const res = await updateMerchantScope(accountCode, body)
    setSaving(false)
    if (res) {
      onUpdated?.()
      onClose?.()
    }
  }

  const onKill = async () => {
    setSaving(true)
    const res = await updateMerchantScope(accountCode, {scope: 'WHITELIST', merchantIds: []})
    setSaving(false)
    if (res) {
      onUpdated?.()
      onClose?.()
    }
  }

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      icon={<GroupOutlinedIcon />}
      iconColor='warning'
      eyebrow='Merchant Scope'
      title={accountCode || ''}
      subtitle='Control which merchants this account code serves'
      width={560}
      footer={
        !loading && (
          <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
            <Tooltip title='Sets whitelist to empty — instant disable without flipping L1 toggle'>
              <Button
                size='small'
                color='warning'
                variant='outlined'
                onClick={onKill}
                disabled={saving}
                startIcon={<PowerSettingsNewIcon fontSize='small' />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 1.75,
                }}
              >
                Kill switch
              </Button>
            </Tooltip>
            <Stack direction='row' spacing={1}>
              <Button
                size='small'
                onClick={onClose}
                disabled={saving}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  color: 'grey.700',
                  px: 1.75,
                }}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                size='small'
                onClick={onSave}
                disabled={saving}
                startIcon={
                  saving ? <CircularProgress size={14} color='inherit' /> : <SaveOutlinedIcon fontSize='small' />
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  px: 2,
                  boxShadow: 'none',
                  '&:hover': {boxShadow: 'none'},
                }}
              >
                Save Changes
              </Button>
            </Stack>
          </Stack>
        )
      }
    >
      {loading && <CenteredLoader />}
      {!loading && status && (
        <Stack spacing={2.5}>
          {/* Currently active banner */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: '#e3f2fd',
              border: '1px solid #bbdefb',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
            }}
          >
            <Avatar sx={{bgcolor: '#bbdefb', color: '#1565c0', width: 32, height: 32}}>
              <CheckCircleIcon sx={{fontSize: 18}} />
            </Avatar>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography sx={{fontSize: '0.68rem', color: '#1565c0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em'}}>
                Currently Active For
              </Typography>
              <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: '#0d47a1'}}>
                {status.l1ActiveFor || '—'}
              </Typography>
            </Box>
          </Paper>

          {/* Scope picker — card-style radios */}
          <Box>
            <Typography
              sx={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'grey.700',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                mb: 1,
              }}
            >
              Scope
            </Typography>
            <Stack spacing={1}>
              <ScopeOption
                selected={scope === 'ALL'}
                onClick={() => setScope('ALL')}
                icon={<PublicIcon />}
                accent='#1565c0'
                bg='#e3f2fd'
                title='All Merchants'
                description='L1 fires for everyone using this account code.'
              />
              <ScopeOption
                selected={scope === 'WHITELIST'}
                onClick={() => setScope('WHITELIST')}
                icon={<FilterAltOutlinedIcon />}
                accent='#e65100'
                bg='#fff3e0'
                title='Selected (Whitelist)'
                description='L1 fires only for the merchant IDs you list below.'
              />
            </Stack>
          </Box>

          {/* Whitelist editor */}
          {scope === 'WHITELIST' && (
            <SectionPaper>
              <SectionHeader
                icon={<GroupOutlinedIcon fontSize='small' />}
                title='Whitelisted Merchants'
                count={whitelist.length}
                color='warning'
              />

              {/* Select-all / clear-selection shortcuts. Sits above the search
                  field so it's the first thing the user sees when they want to
                  bulk-add. "Select all available" pre-fills the picker — user
                  can still remove a few before hitting Add. */}
              <Stack direction='row' spacing={1.5} alignItems='center' sx={{mt: 1.5, mb: 1}}>
                <Button
                  size='small'
                  variant='text'
                  onClick={() => setPicked(availableMerchants.filter((m) => m.merchantId != null))}
                  disabled={availableMerchants.length === 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#1565c0',
                    px: 1,
                    py: 0.25,
                    minWidth: 'auto',
                  }}
                >
                  Select all {availableMerchants.filter((m) => m.merchantId != null).length} available
                </Button>
                {picked.length > 0 && (
                  <>
                    <Typography sx={{fontSize: '0.7rem', color: 'grey.400'}}>•</Typography>
                    <Button
                      size='small'
                      variant='text'
                      onClick={() => setPicked([])}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        color: 'grey.700',
                        px: 1,
                        py: 0.25,
                        minWidth: 'auto',
                      }}
                    >
                      Clear selection ({picked.length})
                    </Button>
                  </>
                )}
              </Stack>

              <Stack direction='row' spacing={1} alignItems='flex-start' sx={{minWidth: 0}}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size='small'
                  // Cap visible chips so a big selection (e.g. all 800
                  // merchants) doesn't blow out the row height or overflow.
                  // Users see "+806 more" — opening the dropdown reveals the
                  // full set with checkmarks.
                  limitTags={5}
                  options={availableMerchants}
                  value={picked}
                  onChange={(_, v) => setPicked(v || [])}
                  getOptionLabel={(o) => {
                    const display = o?.name || o?.companyName || ''
                    if (!display) return ''
                    return o.merchantId != null ? `${display} (${o.merchantId})` : display
                  }}
                  isOptionEqualToValue={(o, v) =>
                    (o?.merchantId != null && o.merchantId === v?.merchantId) ||
                    (o?.id && o.id === v?.id)
                  }
                  renderOption={(props, o, {selected}) => (
                    <Box
                      component='li'
                      {...props}
                      key={o.merchantId ?? o.id}
                      sx={{display: 'flex', alignItems: 'center', gap: 1}}
                    >
                      <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography sx={{fontSize: '0.82rem', fontWeight: 600, color: 'grey.800'}} noWrap>
                          {o.name || o.companyName || '(unnamed)'}
                        </Typography>
                        <Typography sx={{fontSize: '0.68rem', color: 'grey.600'}} noWrap>
                          {o.merchantId != null
                            ? o.companyName && o.name
                              ? `${o.companyName} · ID ${o.merchantId}`
                              : `ID ${o.merchantId}`
                            : 'No numeric merchantId — cannot whitelist'}
                        </Typography>
                      </Box>
                      {selected && <CheckCircleIcon sx={{fontSize: 16, color: '#1565c0'}} />}
                    </Box>
                  )}
                  renderTags={(value, getTagProps) =>
                    // Long merchant names get truncated with ellipsis so a
                    // single chip can't push the row width past the drawer.
                    value.map((o, idx) => (
                      <Chip
                        {...getTagProps({index: idx})}
                        key={o.merchantId ?? o.id}
                        size='small'
                        label={o.name || o.companyName || `ID ${o.merchantId}`}
                        sx={{
                          height: 22,
                          maxWidth: 180,
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          '& .MuiChip-label': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          },
                          '& .MuiChip-deleteIcon': {color: '#1565c0', fontSize: 14},
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={
                        picked.length > 0
                          ? ''
                          : availableMerchants.length === 0
                          ? 'Loading merchants…'
                          : `Search ${availableMerchants.length} merchants — pick multiple`
                      }
                      sx={{'& .MuiOutlinedInput-root': {borderRadius: 2, alignItems: 'center'}}}
                    />
                  )}
                  sx={{
                    flex: 1,
                    minWidth: 0, // lets the autocomplete shrink instead of pushing the Add button off-screen
                    '& .MuiAutocomplete-inputRoot': {flexWrap: 'wrap'},
                  }}
                />
                <Button
                  size='small'
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={addPicked}
                  disabled={!picked || picked.length === 0}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 'none',
                    px: 2,
                    mt: 0.25,
                    minWidth: 96, // wide enough for "Add 999" without truncation
                    flexShrink: 0, // never squeeze the Add button — it's the primary action
                    whiteSpace: 'nowrap',
                    '&:hover': {boxShadow: 'none'},
                  }}
                >
                  {picked && picked.length > 1 ? `Add ${picked.length}` : 'Add'}
                </Button>
              </Stack>
              <Box sx={{mt: 1.5}}>
                {whitelist.length === 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1.5,
                      backgroundColor: '#fff8e1',
                      borderRadius: 1.5,
                      border: '1px dashed',
                      borderColor: '#ffe082',
                    }}
                  >
                    <FilterAltOutlinedIcon sx={{fontSize: 18, color: '#e65100'}} />
                    <Typography sx={{fontSize: '0.75rem', color: '#bf6b00'}}>
                      No merchants in whitelist — L1 is effectively disabled.
                    </Typography>
                  </Box>
                ) : (
                  <CommittedWhitelistChips
                    whitelist={whitelist}
                    merchantById={merchantById}
                    onRemove={removeId}
                    onClearAll={() => setWhitelist([])}
                  />
                )}
              </Box>
            </SectionPaper>
          )}
        </Stack>
      )}
    </DrawerShell>
  )
}

/* ───────────────────────── Shared internals ───────────────────────── */

function DrawerShell({
  open,
  onClose,
  icon,
  iconColor = 'primary',
  eyebrow,
  title,
  subtitle,
  width = 480,
  footer,
  children,
}) {
  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      PaperProps={{sx: {width, backgroundColor: '#fafbfd'}}}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        {/* Header */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: '1px solid #eeeeee',
            backgroundColor: '#fff',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Avatar
            sx={{
              bgcolor: `${iconColor}.light`,
              color: `${iconColor}.dark`,
              width: 44,
              height: 44,
            }}
          >
            {icon}
          </Avatar>
          <Box sx={{flex: 1, minWidth: 0}}>
            {eyebrow && (
              <Typography
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  color: 'grey.600',
                  lineHeight: 1.2,
                }}
              >
                {eyebrow}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: '1.05rem',
                fontWeight: 700,
                color: 'grey.800',
                lineHeight: 1.25,
                mt: eyebrow ? 0.25 : 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{fontSize: '0.74rem', color: 'grey.600', mt: 0.25}}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size='small'
            sx={{
              borderRadius: 1.5,
              '&:hover': {backgroundColor: '#f5f5f5'},
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        {/* Body — scrollable */}
        <Box sx={{flex: 1, overflowY: 'auto', p: 2.5}}>{children}</Box>

        {/* Optional footer pinned to bottom */}
        {footer && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #eeeeee',
              backgroundColor: '#fff',
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Drawer>
  )
}

function SectionPaper({children, sx = {}}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: '1px solid #eeeeee',
        backgroundColor: '#fff',
        ...sx,
      }}
    >
      {children}
    </Paper>
  )
}

function SectionHeader({icon, title, count, color = 'primary'}) {
  return (
    <Stack direction='row' spacing={1.25} alignItems='center'>
      <Avatar
        sx={{
          bgcolor: `${color}.light`,
          color: `${color}.dark`,
          width: 28,
          height: 28,
        }}
      >
        {icon}
      </Avatar>
      <Typography sx={{flex: 1, fontSize: '0.85rem', fontWeight: 700, color: 'grey.800'}}>
        {title}
      </Typography>
      {typeof count === 'number' && (
        <Badge
          badgeContent={count}
          color={count > 0 ? color : 'default'}
          max={999}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.65rem',
              fontWeight: 700,
              height: 18,
              minWidth: 18,
              borderRadius: '9px',
            },
          }}
        >
          <Box sx={{width: 8, height: 8}} />
        </Badge>
      )}
    </Stack>
  )
}

function KpiTile({label, value, accent, bg}) {
  const v = value || 0
  const muted = v === 0
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        border: '1px solid',
        borderColor: muted ? '#eeeeee' : `${accent}25`,
        backgroundColor: muted ? '#fafafa' : bg,
      }}
    >
      <Typography
        sx={{
          fontSize: '0.62rem',
          fontWeight: 700,
          color: muted ? 'grey.600' : accent,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: '1.25rem',
          fontWeight: 800,
          color: muted ? 'grey.500' : 'grey.800',
          lineHeight: 1.2,
          mt: 0.25,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {muted ? '—' : v.toLocaleString()}
      </Typography>
    </Box>
  )
}

function ScopeOption({selected, onClick, icon, accent, bg, title, description}) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: '1.5px solid',
        borderColor: selected ? accent : '#eeeeee',
        backgroundColor: selected ? bg : '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        transition: 'border-color 0.12s ease, background-color 0.12s ease',
        '&:hover': {
          borderColor: selected ? accent : '#cfd8dc',
        },
      }}
    >
      <Avatar
        sx={{
          bgcolor: bg,
          color: accent,
          width: 36,
          height: 36,
          border: `1px solid ${accent}25`,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: '0.85rem', fontWeight: 700, color: 'grey.800'}}>
          {title}
        </Typography>
        <Typography sx={{fontSize: '0.72rem', color: 'grey.600', mt: 0.25, lineHeight: 1.4}}>
          {description}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          border: `2px solid ${selected ? accent : '#cfd8dc'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {selected && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: accent,
            }}
          />
        )}
      </Box>
    </Paper>
  )
}

function CenteredLoader() {
  return (
    <Stack alignItems='center' sx={{py: 6}}>
      <CircularProgress size={24} />
    </Stack>
  )
}

/**
 * Render the committed whitelist as chips. Caps the visible chips at
 * COLLAPSED_LIMIT to keep the drawer usable with hundreds of merchants —
 * blowing 800 chips into the DOM is both slow and visually unreadable.
 * "Show all" expands; "Clear all" wipes the entire whitelist (with a
 * separate kill-switch button for instant disable still available at the
 * drawer footer).
 */
function CommittedWhitelistChips({whitelist, merchantById, onRemove, onClearAll}) {
  const COLLAPSED_LIMIT = 30
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? whitelist : whitelist.slice(0, COLLAPSED_LIMIT)
  const hiddenCount = whitelist.length - visible.length

  return (
    <Box>
      <Stack
        direction='row'
        spacing={0.75}
        flexWrap='wrap'
        useFlexGap
        sx={{
          maxHeight: expanded ? 320 : 'none',
          overflowY: expanded ? 'auto' : 'visible',
          pr: expanded ? 0.5 : 0,
        }}
      >
        {visible.map((id) => {
          const m = merchantById[id]
          const display = m?.name || m?.companyName
          const label = display ? `${display} (${id})` : `ID ${id}`
          return (
            <Chip
              key={id}
              size='small'
              label={label}
              onDelete={() => onRemove(id)}
              sx={{
                borderRadius: '16px',
                fontWeight: 600,
                fontSize: '0.72rem',
                backgroundColor: '#fff3e0',
                color: '#e65100',
                maxWidth: 240,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiChip-deleteIcon': {
                  color: '#e65100',
                  fontSize: 16,
                  '&:hover': {color: '#bf360c'},
                },
              }}
            />
          )
        })}
      </Stack>

      {(hiddenCount > 0 || expanded || whitelist.length > 0) && (
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{mt: 1}}>
          {hiddenCount > 0 && !expanded && (
            <Button
              size='small'
              variant='text'
              onClick={() => setExpanded(true)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.72rem',
                color: '#e65100',
                px: 1,
                py: 0.25,
                minWidth: 'auto',
              }}
            >
              Show all {whitelist.length} ({hiddenCount} more)
            </Button>
          )}
          {expanded && (
            <Button
              size='small'
              variant='text'
              onClick={() => setExpanded(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.72rem',
                color: 'grey.700',
                px: 1,
                py: 0.25,
                minWidth: 'auto',
              }}
            >
              Show less
            </Button>
          )}
          {whitelist.length > 0 && (
            <>
              <Typography sx={{fontSize: '0.7rem', color: 'grey.400'}}>•</Typography>
              <Button
                size='small'
                variant='text'
                onClick={onClearAll}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  color: '#c62828',
                  px: 1,
                  py: 0.25,
                  minWidth: 'auto',
                }}
              >
                Clear all ({whitelist.length})
              </Button>
            </>
          )}
        </Stack>
      )}
    </Box>
  )
}

function EmptyState({icon, title, subtitle}) {
  return (
    <Stack alignItems='center' sx={{py: 6, px: 3, textAlign: 'center'}} spacing={1}>
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          backgroundColor: '#f5f5f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'grey.500',
        }}
      >
        {icon}
      </Box>
      <Typography sx={{fontSize: '0.92rem', fontWeight: 700, color: 'grey.800'}}>{title}</Typography>
      {subtitle && (
        <Typography sx={{fontSize: '0.78rem', color: 'grey.600', maxWidth: 360, lineHeight: 1.5}}>
          {subtitle}
        </Typography>
      )}
    </Stack>
  )
}
